/**
 * @fileoverview O Pix de uma alteração aguardando pagamento (31/10/2026, etapa
 * 8 dos planos), no modal e em Faturamento: o valor, até quando vale, o QR
 * Code desenhado a partir do código (a imagem da Pagar.me só se o código não
 * vier), o código visível e o botão de copiar. No celular (o mesmo aparelho do
 * banco), copiar vem primeiro e é o botão principal; o QR fica embaixo. Vencido,
 * o QR e o copiar saem: fica "Este Pix venceu às HH:MM" (e, em Faturamento, o
 * que fazer).
 */

import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CircularProgress from "@material-ui/core/CircularProgress";
import QRCode from "qrcode.react";
import { Clock, Copy } from "lucide-react";
import { toast } from "react-toastify";

import { i18n } from "../../../translate/i18n";
import { money } from "../../../utils/billingPlans";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { formatTime } from "../../../utils/formatLocaleDate";

const t = (key, options) => i18n.t(`plansDialog.${key}`, options);

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const soft = dark ? "rgba(255,255,255,0.03)" : "#FAFAFA";
  const focus = `0 0 0 1px ${dark ? "rgba(255,255,255,0.7)" : "rgba(9,9,11,0.7)"}`;
  return {
    root: {
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr)",
      gap: 20,
      alignItems: "center",
      width: "100%",
      maxWidth: 560,
      textAlign: "left",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr", gap: 14 },
    },
    // O QR sempre em fundo branco (o leitor do banco não lê no escuro), com uma borda fina.
    qr: {
      background: "#fff",
      border: `1px solid ${line}`,
      borderRadius: 10,
      padding: 10,
      lineHeight: 0,
      justifySelf: "center",
      [theme.breakpoints.down("xs")]: { order: 2 },
    },
    details: { display: "flex", flexDirection: "column", gap: 10, minWidth: 0 },
    amount: { fontSize: 26, fontWeight: 700, letterSpacing: "-0.01em", color: text, fontVariantNumeric: "tabular-nums" },
    valid: { fontSize: 13, color: muted, fontVariantNumeric: "tabular-nums" },
    codeLabel: { fontSize: 12.5, fontWeight: 600, color: text },
    code: {
      fontSize: 12,
      lineHeight: 1.5,
      color: muted,
      background: soft,
      border: `1px solid ${line}`,
      borderRadius: 8,
      padding: "8px 10px",
      wordBreak: "break-all",
      maxHeight: 64,
      overflowY: "auto",
      userSelect: "all",
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    copy: {
      alignSelf: "flex-start",
      height: 40,
      borderRadius: 9,
      border: `1px solid ${line}`,
      background: "transparent",
      color: text,
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      padding: "0 16px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      "& svg": { width: 15, height: 15 },
      "&:hover": { background: soft },
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    copyMain: {
      alignSelf: "stretch",
      justifyContent: "center",
      height: 44,
      border: "none",
      background: "#3D5CFD",
      color: "#fff",
      fontWeight: 700,
      "&:hover": { background: "#3D5CFD" },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${dark ? "#fff" : "#09090B"}` },
    },
    waiting: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: muted },
    generating: { display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: muted, padding: "12px 0" },
    expired: { display: "flex", flexDirection: "column", gap: 4, fontSize: 14, lineHeight: 1.5, color: text, padding: "8px 0" },
    expiredHint: { color: muted, fontSize: 13 },
  };
});

const PixPayment = ({ pix, amountCents, waiting = true, expiredHint = "" }) => {
  const classes = useStyles();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const expiresAt = pix?.expiresAt ? new Date(pix.expiresAt).getTime() : null;
  const [expired, setExpired] = useState(() => !!expiresAt && expiresAt <= Date.now());

  // Vence na hora certa, sem esperar outra leitura do pedido.
  useEffect(() => {
    if (!expiresAt) return undefined;
    const wait = expiresAt - Date.now();
    setExpired(wait <= 0);
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setExpired(true), wait);
    return () => clearTimeout(timer);
  }, [expiresAt]);

  if (expired) {
    return (
      <div className={classes.expired} role="status">
        <span>{t("pixExpiredAt", { time: formatTime(pix.expiresAt) })}</span>
        {expiredHint && <span className={classes.expiredHint}>{expiredHint}</span>}
      </div>
    );
  }

  if (!pix?.qrCode && !pix?.qrCodeUrl) {
    return (
      <div className={classes.generating} role="status">
        <CircularProgress size={16} color="inherit" aria-hidden="true" />
        {t("pixGenerating")}
      </div>
    );
  }

  const copy = async () => {
    if (await copyToClipboard(pix.qrCode)) toast.success(t("pixCopied"));
    else toast.info(t("pixCopyManual"));
  };

  return (
    <div className={classes.root}>
      <div className={classes.qr}>
        {pix.qrCode ? (
          <QRCode value={pix.qrCode} size={phone ? 168 : 184} level="M" renderAs="svg" aria-label={t("pixQrAlt")} role="img" />
        ) : (
          <img src={pix.qrCodeUrl} alt={t("pixQrAlt")} width={184} height={184} />
        )}
      </div>
      <div className={classes.details}>
        <span className={classes.amount}>{money(amountCents)}</span>
        {pix.expiresAt && <span className={classes.valid}>{t("pixValidUntil", { time: formatTime(pix.expiresAt) })}</span>}
        {pix.qrCode && (
          <>
            <button type="button" className={`${classes.copy} ${phone ? classes.copyMain : ""}`} onClick={copy}>
              <Copy />
              {t("pixCopy")}
            </button>
            <span className={classes.codeLabel} id="pix-code-label">
              {t("pixCode")}
            </span>
            <div className={classes.code} aria-labelledby="pix-code-label" tabIndex={0}>
              {pix.qrCode}
            </div>
          </>
        )}
        {waiting && (
          <span className={classes.waiting} role="status">
            {reduceMotion ? <Clock size={14} aria-hidden="true" /> : <CircularProgress size={14} color="inherit" aria-hidden="true" />}
            {t("pixWaiting")}
          </span>
        )}
      </div>
    </div>
  );
};

export default PixPayment;
